"use client";

import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";
import { useTasksStore } from "@/stores/tasks-store";

interface QuickTaskModalProps {
  open: boolean;
  onClose: () => void;
  documentId: string | undefined;
}

/**
 * Modal de creación rápida de tarea vinculada al documento activo.
 * Crea la tarea via API (no es local-only como el resto del store).
 */
export function QuickTaskModal({ open, onClose, documentId }: QuickTaskModalProps) {
  const createTask = useTasksStore((s) => s.createTask);
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState<"low" | "medium" | "high">("medium");
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!documentId || !trimmed || creating) return;
    setCreating(true);
    try {
      await createTask({ document_id: documentId, title: trimmed, priority });
      toast.success("Tarea creada", `Se añadió "${trimmed}" a la lista.`);
      setTitle("");
      setPriority("medium");
      onClose();
    } catch (err) {
      toast.error("No se pudo crear la tarea", err instanceof Error ? err.message : "");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Crear tarea en este documento"
      description="Vinculada automáticamente al documento abierto"
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="quick-task-title"
            className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
          >
            Título
          </label>
          <input
            id="quick-task-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ej. Revisar la sección de onboarding"
            autoFocus
            required
            className="bg-background border border-border/40 rounded-xl px-3 py-2 text-xs outline-none text-foreground focus:border-primary"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="quick-task-priority"
            className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
          >
            Prioridad
          </label>
          <select
            id="quick-task-priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as "low" | "medium" | "high")}
            className="bg-background border border-border/40 rounded-xl px-3 py-2 text-xs outline-none text-foreground cursor-pointer"
          >
            <option value="low">Baja</option>
            <option value="medium">Media</option>
            <option value="high">Alta</option>
          </select>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border/20 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 bg-secondary hover:bg-secondary/80 rounded-xl text-xs font-bold text-foreground transition-all border border-border/40"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!title.trim() || creating}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-xl text-xs font-bold hover:bg-primary/90 transition-all shadow-md shadow-primary/10 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {creating && <Loader2 className="w-3 h-3 animate-spin" />}
            Crear tarea
          </button>
        </div>
      </form>
    </Modal>
  );
}
